//#region Libraries
import { useState } from "react";
//#endregion
//#region Styles
import { Column, SubtitleSection } from "components/styles";
import { Paragraph } from "views/RefuerzoAcademico/components/AcadReinforceAttendance/styles";
import { Container } from "./styles";
//#endregion
//#region Components
import CustomTabs from "components/CustomTabs/CustomTabs";
import DateListOfWeek from "./DateListOfWeek/DateListOfWeek";
import DialogAlertAction from "components/DialogAlertAction/DialogAlertAction";
import CustomButton from "components/CustomButton/CustomButton";
import Preloader from "components/Preloader/Preloader";
import DialogAlertUpdateDate from "./DialogAlertUpdateDate/DialogAlertUpdateDate";
//#endregion
//#region Services
import {
	requestDeleteDateEventById,
	requestUpdateDateEventById,
} from "services/events/aboutEvents";
//#endregion
//#region Utils
import useMainContext from "utils/context/useMainContext";
import {
	getDateMonthString,
	getDatePartsByMillis,
} from "components/CustomDatePicker/utils/helpers";
import { TIMEOUT_HIDDEN_TOAST_MESSAGE } from "components/ToastMessage/ToastMessage";
//#endregion

const INIT_CURRENT_DATE_CARRY_OUT_AN_ACTION = {
	id: 0,
	title: "",
	date: {
		value: -1,
		formatted: "",
	},
	time: {
		value: {
			start: -1,
			end: -1,
		},
		formatted: "",
	},
};

const EventDurationDetail = ({
	isTeacherUser,
	title,
	eventDateListGroup = [],
	setToastMessage,
	fillGroupingDateList,
	fillEventDataToAttendance,
}) => {
	const mainContext = useMainContext();
	//#region States
	const [currentTabIdx, setCurrentTabIdx] = useState(0);
	const [showDialogDeleteDate, setShowDialogDeleteDate] = useState(false);
	const [showDialogUpdateDate, setShowDialogUpdateDate] = useState(false);
	const [loadingService, setLoadingService] = useState({
		isLoading: false,
		message: "",
	});
	const [currentDateCarryOutAnAction, setCurrentDateCarryOutAnAction] =
		useState({
			...INIT_CURRENT_DATE_CARRY_OUT_AN_ACTION,
		});
	//#endregion
	//#region Functions
	const toggleShowDialogDeleteDate = () => {
		setShowDialogDeleteDate(prev => !prev);
	};
	const toggleShowDialogUpdateDate = () => {
		setShowDialogUpdateDate(prev => !prev);
	};
	const askIfItWillBeDeleted = ({ id, date, time }) => {
		setCurrentDateCarryOutAnAction({
			id,
			title,
			date,
			time,
		});
		toggleShowDialogDeleteDate();
	};
	const askIfItWillBeUpdated = ({ id, date, time }) => {
		setCurrentDateCarryOutAnAction({
			id,
			title,
			date,
			time,
		});
		toggleShowDialogUpdateDate();
	};
	const changeCurrentTabIdx = idx => {
		setCurrentTabIdx(idx);
	};
	const renderDeleteDateDialogContent = () => {
		const { title, time, date } = currentDateCarryOutAnAction;
		const text = `${date.formatted} de ${time.formatted} de ${title}`;
		return (
			<Paragraph className="ellipsis">
				<span title={text}>{text}</span>
				<br />
				Este día se eliminará, sin opción a recuperarla
			</Paragraph>
		);
	};
	const getSuccessMessageToCarryOutActionOnEventDate = verb => {
		const monthDate = getDatePartsByMillis(
			currentDateCarryOutAnAction.date.value
		).splice(1, 3);
		return `Se ${verb} el día ${getDateMonthString(
			monthDate,
			1
		)} del evento "${title}"`;
	};
	const deleteEventDate = async () => {
		toggleShowDialogDeleteDate();
		// Formando objeto de carga
		const loadingObj = {
			message: "Eliminando...",
			isLoading: true,
		};
		// Abriendo preloader
		setLoadingService({ ...loadingObj });
		// Realizando petición de eliminación de evento
		const success = await requestDeleteDateEventById(
			currentDateCarryOutAnAction.id
		);
		// Gestionando resultado de la operación
		if (!success) {
			mainContext.setShowDialogUnexpectedError(true);
			return;
		}
		// Recargando detalle de días del evento
		loadingObj.message = "Cargando...";
		setLoadingService({ ...loadingObj });
		await fillGroupingDateList();
		// Cerrando preloader
		loadingObj.isLoading = false;
		setLoadingService({ ...loadingObj });
		// Abriendo toast message para avisar de la eliminación exitosa
		setToastMessage({
			hidden: false,
			message: getSuccessMessageToCarryOutActionOnEventDate("eliminó"),
		});
		// Programando cierre automático del toast message
		setTimeout(() => {
			setToastMessage(prev => ({
				...prev,
				hidden: true,
			}));
		}, TIMEOUT_HIDDEN_TOAST_MESSAGE);
	};
	const updateEventDate = async () => {
		//#region Mensajes
		// ERR_CRUCE_HORARIO
		// OK
		// ERROR
		// CUENTA_INACTIVA
		// CUENTA_DOCENTE_NO_EXISTE
		// CUENTA_DOCENTE_INACTIVA
		// CUENTA_DOC_DIFERENTES_PRIVILEGIOS
		//#endregion
		// Formando objeto de carga
		const loadingObj = {
			message: "Actualizando...",
			isLoading: true,
		};
		// Abriendo preloader
		setLoadingService({ ...loadingObj });
		const { id, time, date } = currentDateCarryOutAnAction;
		const { success, message } = await requestUpdateDateEventById({
			id,
			date: date.value,
			time: {
				start: time.value.start,
				end: time.value.end,
			},
		});
		try {
			// Gestionando resultado de la operación
			if (message !== "ERR_CRUCE_HORARIO" && !success) {
				mainContext.setShowDialogUnexpectedError(true);
				return null;
			}
			if (message === "ERR_CRUCE_HORARIO")
				return "INVALID_CROSSING_SCHEDULE";
			if (!success) {
				mainContext.setShowDialogUnexpectedError(true);
				return null;
			}
			toggleShowDialogUpdateDate();
			// Recargando detalle de días del evento
			loadingObj.message = "Cargando...";
			setLoadingService({ ...loadingObj });
			await fillGroupingDateList();
		} finally {
			// Cerrando preloader
			loadingObj.isLoading = false;
			setLoadingService({ ...loadingObj });
		}
		// Abriendo toast message para avisar de la actualización exitosa
		setToastMessage({
			hidden: false,
			message: getSuccessMessageToCarryOutActionOnEventDate("reprogramó"),
		});
		// Programando cierre automático del toast message
		setTimeout(() => {
			setToastMessage(prev => ({
				...prev,
				hidden: true,
			}));
		}, TIMEOUT_HIDDEN_TOAST_MESSAGE);
		return null;
	};
	const getCurrentTab = () => {
		const weeks = Object.values(eventDateListGroup.future.weeks);
		// Validando si el apuntador del índice de tabs existe en la semana
		if (!(currentTabIdx in weeks)) {
			setCurrentTabIdx(0);
			return weeks[0];
		}
		return weeks[currentTabIdx];
	};
	//#endregion
	return (
		<Container>
			<Preloader
				hidden={!loadingService.isLoading}
				message={loadingService.message}
			/>
			{eventDateListGroup.currentWeek.map((weekGroup, idx) => {
				return (
					<DateListOfWeek
						key={idx}
						isTeacherUser={isTeacherUser}
						askIfItWillBeDeleted={askIfItWillBeDeleted}
						askIfItWillBeUpdated={askIfItWillBeUpdated}
						fillEventDataToAttendance={fillEventDataToAttendance}
						{...weekGroup}
					/>
				);
			})}
			{eventDateListGroup.future.months.length > 0 && (
				<>
					<SubtitleSection
						margin="15px 0 0"
						fontSize="16px"
					>
						Días próximos de evento
					</SubtitleSection>
					<CustomTabs
						currentTabIdx={currentTabIdx}
						changeCurrentTabIdx={changeCurrentTabIdx}
						tabWidth={90}
						viewTabTitles={eventDateListGroup.future.months}
					/>
					<Column>
						{getCurrentTab().map((weekGroup, idx) => {
							return (
								<DateListOfWeek
									key={idx}
									{...weekGroup}
									isTeacherUser={isTeacherUser}
									fillEventDataToAttendance={
										fillEventDataToAttendance
									}
									askIfItWillBeDeleted={askIfItWillBeDeleted}
									askIfItWillBeUpdated={askIfItWillBeUpdated}
								/>
							);
						})}
					</Column>
				</>
			)}
			<DialogAlertAction
				minWidth="500px"
				show={showDialogDeleteDate}
				toggleShow={toggleShowDialogDeleteDate}
				variant="secondary"
				title="¿Estás seguro de eliminar este día del evento?"
				contentComponent={renderDeleteDateDialogContent()}
			>
				<CustomButton
					variant="secondary"
					width="125px"
					text="Si, eliminar"
					onClick={deleteEventDate}
				/>
				<CustomButton
					width="125px"
					text="No, cancelar"
					onClick={() => toggleShowDialogDeleteDate(false)}
				/>
			</DialogAlertAction>
			<DialogAlertUpdateDate
				show={showDialogUpdateDate}
				toggleShow={toggleShowDialogUpdateDate}
				currentDateCarryOutAnAction={currentDateCarryOutAnAction}
				setCurrentDateCarryOutAnAction={setCurrentDateCarryOutAnAction}
				updateEventDate={updateEventDate}
			/>
		</Container>
	);
};

export default EventDurationDetail;
